import { useMemo, useState } from 'react';
import { CommonDatagrid } from '..';
import { getAdminUsersColumns } from './columns';
import { useDispatch, useSelector } from 'react-redux';
import { IAdminUser, IRootState } from '../../../types/store';
import { Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CREATE_USER_ROUTE } from '../../../constants/routes';
import { AdminUsersService } from '../../../api/services/adminUsersService';
import {
  setAdminUsers,
  toggleAdminUserEnabled,
  updateAdminUser,
} from '../../../store/Reducers/AdminUsersSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { RefreshButton } from '../../Buttons/RefreshButton';
import { EditAdminUserDialog } from '../../Dialog/EditAdminUserDialog';
import { ViewAdminUserDialog } from '../../Dialog/ViewAdminUserDialog';
import { TemporaryPasswordDialog } from '../../Dialog/TemporaryPasswordDialog';
import { ITemporaryPasswordResponse } from '../../../types/api/response/adminusersresponse';

export const AdminUsersDatagrid = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { content: data } = useSelector((state: IRootState) => state.adminUsers);

  const [selectedUser, setSelectedUser] = useState<IAdminUser | null>(null);
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  const [isOpenViewDialog, setIsOpenViewDialog] = useState(false);
  const [togglingEnabledId, setTogglingEnabledId] = useState<number | null>(null);
  const [resettingPasswordId, setResettingPasswordId] = useState<number | null>(null);
  const [settingTempPasswordId, setSettingTempPasswordId] = useState<number | null>(null);
  const [tempPasswordResult, setTempPasswordResult] = useState<ITemporaryPasswordResponse | null>(
    null,
  );
  const [isOpenTempPasswordDialog, setIsOpenTempPasswordDialog] = useState(false);

  const reload = async () => {
    const res = await AdminUsersService.getAdminUsers();
    dispatch(setAdminUsers(res));
  };

  const onClickView = (user: IAdminUser) => {
    setSelectedUser(user);
    setIsOpenViewDialog(true);
  };

  const onClickEdit = (user: IAdminUser) => {
    setSelectedUser(user);
    setIsOpenEditDialog(true);
  };

  const onClickToggleEnabled = async (user: IAdminUser) => {
    setTogglingEnabledId(user.id);
    const newEnabled = !user.enabled;
    try {
      await AdminUsersService.toggleEnabled({ id: user.id, enabled: newEnabled });
      dispatch(toggleAdminUserEnabled({ id: user.id, enabled: newEnabled }));
      dispatch(
        showNotification({
          message: `User "${user.username}" ${newEnabled ? 'activated' : 'deactivated'}.`,
        }),
      );
    } catch (error: any) {
      dispatch(
        showNotification({
          message: error?.response?.data?.message || error?.message || 'Failed to update status.',
          severity: 'error',
        }),
      );
    } finally {
      setTogglingEnabledId(null);
    }
  };

  const onClickResetPassword = async (user: IAdminUser) => {
    setResettingPasswordId(user.id);
    try {
      await AdminUsersService.resetPassword({ id: user.id });
      dispatch(
        showNotification({
          message: `Password reset for "${user.username}". They will be prompted to change it on next login.`,
        }),
      );
    } catch (error: any) {
      dispatch(
        showNotification({
          message: error?.response?.data?.message || error?.message || 'Failed to reset password.',
          severity: 'error',
        }),
      );
    } finally {
      setResettingPasswordId(null);
    }
  };

  const onClickSetTemporaryPassword = async (user: IAdminUser) => {
    setSettingTempPasswordId(user.id);
    try {
      const result = await AdminUsersService.setTemporaryPassword({ id: user.id });
      setTempPasswordResult(result);
      setIsOpenTempPasswordDialog(true);
    } catch (error: any) {
      dispatch(
        showNotification({
          message:
            error?.response?.data?.message || error?.message || 'Failed to set temporary password.',
          severity: 'error',
        }),
      );
    } finally {
      setSettingTempPasswordId(null);
    }
  };

  const columns = useMemo(
    () =>
      getAdminUsersColumns({
        onClickView,
        onClickEdit,
        onClickToggleEnabled,
        onClickResetPassword,
        onClickSetTemporaryPassword,
        togglingEnabledId,
        resettingPasswordId,
        settingTempPasswordId,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [togglingEnabledId, resettingPasswordId, settingTempPasswordId],
  );

  return (
    <>
      <Box
        sx={{
          // CommonDatagrid (src/components/DataGrid/index.tsx:97-108) wraps its
          // table in its own overflowX:'auto' box sized via a hardcoded
          // `columns.length * 150` guess — this duplicates the horizontal
          // scrolling MUI's DataGrid already does internally, causing two
          // scrollbars once a grid has enough columns to expose the mismatch.
          // Scoped to this page only (not fixed in CommonDatagrid itself, which
          // is shared by 7 other grids) — cancels the outer wrapper's own
          // scroll/width so only the DataGrid's native scrollbar remains.
          // Fragile: relies on CommonDatagrid always rendering exactly 3 direct
          // child Boxes for this call (search bar + header actions + scroll
          // wrapper) — if that internal structure changes, this override
          // silently stops matching.
          '& > .MuiBox-root:nth-of-type(3)': {
            overflowX: 'visible !important',
            width: '100% !important',
          },
          '& > .MuiBox-root:nth-of-type(3) > .MuiBox-root': {
            width: '100% !important',
          },
        }}
      >
        <CommonDatagrid
          data={data}
          columns={columns}
          rowId={'id'}
          searchbarPlaceholder={'Search user by username...'}
          searchKey="username"
          headerActionButtons={
            <Box display="flex" sx={{ gap: 2 }}>
              <RefreshButton onClick={reload} />
              <Button
                style={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
                variant="contained"
                onClick={() => navigate(CREATE_USER_ROUTE)}
              >
                Enroll User
              </Button>
            </Box>
          }
        />
      </Box>

      {selectedUser && (
        <>
          <EditAdminUserDialog
            selectedUser={selectedUser}
            isOpenDialog={isOpenEditDialog}
            setIsOpenDialog={setIsOpenEditDialog}
            onUpdated={(updated) => dispatch(updateAdminUser(updated))}
          />
          <ViewAdminUserDialog
            selectedUser={selectedUser}
            isOpenDialog={isOpenViewDialog}
            setIsOpenDialog={setIsOpenViewDialog}
          />
        </>
      )}

      {tempPasswordResult && (
        <TemporaryPasswordDialog
          result={tempPasswordResult}
          isOpenDialog={isOpenTempPasswordDialog}
          setIsOpenDialog={(open) => {
            setIsOpenTempPasswordDialog(open);
            if (!open) setTempPasswordResult(null);
          }}
        />
      )}
    </>
  );
};
